import { useContext, useEffect, useMemo, useState } from 'react'

import {
  THRCardEmbed,
  useHealthToolsCache,
  ThrForm,
  ThrFormValue,
  ThrFormProps,
  THRCardEmbedProps,
} from '@hhgtech/hhg-components'
import { QUERY_HEALTH_TOOL } from '@hhgtech/hhg-components/constants'
import { getURLwithSSOTracking } from '@hhgtech/hhg-components/ssoV2'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'

import { callApiNew } from 'api/axios'
import { formatTHRContent } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { domainLocales } from 'domainLocales'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  API_DATE_FORMAT,
  THRINFO_COOKIE_KEY,
  basePath,
} from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { HealthToolPositionType, ThrInfo } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { formatGenderUrl } from 'utils/format'

import { HealthToolCardEmbedProps } from '../types'

export type InputError = {
  age: string
  intensity: string
}

type CommonProps = {
  positionType?: HealthToolPositionType
} & HealthToolCardEmbedProps

export type THRCardWrapperProps =
  | ({ onlyUseForm: true } & Partial<ThrFormProps> & CommonProps)
  | ({ onlyUseForm?: false } & Partial<THRCardEmbedProps> & CommonProps)
export const THRCardWrapper = ({
  showUserResult: _showUserResult,
  isSide,
  onlyUseForm,
  ...props
}: THRCardWrapperProps) => {
  const isMobile = useIsMobile()
  const router = useRouter()
  const currentUrl = useCurrentUrl()
  const [thrContent, setThrContent] = useState<ThrInfo>({} as ThrInfo)

  const { state } = useContext(AppContext) || {}

  const { isLoggedIn } = state || {}

  const {
    locale,
    router: { defaultLocale = 'en-PH' },
  } = useRouterExtend()

  const { getHealthToolLink } = useHealthToolsCache(locale)
  const toolLink = useMemo(() => getHealthToolLink(HEALTH_TOOL_TYPE.THR), [
    getHealthToolLink,
  ])

  useEffect(() => {
    ;(async () => {
      const res = await callApiNew(PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_THR_INFO)
      setThrContent(formatTHRContent(res?.data) as ThrInfo)
    })()
  }, [])

  const onSubmit = (values: ThrFormValue) => {
    const { heartRate, gender, age: ageProp, activeValue } = values
    const level = activeValue
    const age = dayjs(ageProp).format(API_DATE_FORMAT)

    const thrInfo = {
      gender,
      age,
      rate: heartRate,
      intensity: activeValue,
      source_url: window.location.href,
    }

    // set `thrInfo`
    Cookies.set(THRINFO_COOKIE_KEY, JSON.stringify(thrInfo), {
      domain: `.${domainLocales[locale || defaultLocale]}`,
      path: '/',
    })
    ;(async () => {
      const redirectPageUrl = toolLink

      router.push(
        {
          ...router,
          query: {
            ...router.query,
            gender: gender || '',
            birthday: ageProp ? dayjs(ageProp).format('MM-DD-YYYY') : '',
          },
        },
        undefined,
        { shallow: true },
      )

      const destination = `${
        typeof window !== 'undefined' ? window.location.origin : ''
      }${basePath}${redirectPageUrl}?${
        QUERY_HEALTH_TOOL.gender
      }=${formatGenderUrl(gender as string)}&${QUERY_HEALTH_TOOL.age}=${age}&${
        QUERY_HEALTH_TOOL.rate
      }=${heartRate}&${QUERY_HEALTH_TOOL.intensity}=${level}`

      if (!isLoggedIn) {
        redirectToSSOWithReturnUrl(destination, 'target-heart-rate')
      } else {
        router.push(
          getURLwithSSOTracking(destination, { source: 'target-heart-rate' })
            ?.href || destination,
        )
      }
    })()
  }

  if (!toolLink)
    return (
      <div
        style={{
          height: 500,
        }}
      />
    )

  return onlyUseForm ? (
    <ThrForm
      onSubmit={onSubmit}
      formLayout={isMobile || isSide ? 'default' : 'inline'}
      submitProps={{
        dataEventLabel: currentUrl,
      }}
      {...props}
    />
  ) : (
    <THRCardEmbed
      thrContent={thrContent}
      isSide={isSide}
      formProps={{
        onSubmit,
        formLayout: isMobile || isSide ? 'default' : 'inline',
        submitProps: {
          dataEventLabel: currentUrl,
        },
      }}
      {...props}
    />
  )
}
